import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import { Button } from '../../../../shared/components/button/Button';
import { formatCurrency } from '../../../../shared/utils/helpers/numbers';
import DevolucaoController from '../../../../controllers/DevolucaoController';
import TipoPagamentoController from '../../../../controllers/TipoPagamentoController';
import CondicaoPagamentoController from '../../../../controllers/CondicaoPagamentoController';
import { MenuItem, Paper, TextField as Select } from '@mui/material';
import {
  condicaoEhAvista,
  condicoesPermitidas,
  descricaoEhFormaAvista,
} from '../../../../shared/utils/helpers/formaCondicaoPagamento';

/** Hoje em yyyy-MM-dd, no fuso local — toISOString() devolveria UTC e viraria ontem. */
const hoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

interface Props {
  aberto: boolean;
  vendaId: number | null;
  aoFechar: (houveMudanca: boolean) => void;
}

interface ItemLinha {
  id_item_origem: number;
  prod_id: number;
  descricao: string;
  qtd_original: number;
  qtd_devolvida: number;
  saldo_devolvivel: number;
  vndprod_total: number;
  bonificado: boolean;
  quantidade: number; // o que o operador escolheu
}

/**
 * Devolução de venda, do rascunho à confirmação.
 *
 * DUAS ETAPAS, DE PROPÓSITO:
 *   1. escolher itens e escrever o motivo — nada é gravado até clicar em Continuar;
 *   2. ver o que vai acontecer (inclusive quais boletos serão baixados no banco) e
 *      confirmar.
 *
 * A etapa 2 existe porque baixa de boleto NÃO TEM VOLTA. O operador precisa ver a lista
 * antes, não depois.
 *
 * CADA LINHA É UMA LINHA, não um produto: produção tem pedidos com o mesmo produto em duas
 * linhas (238, 458, 1037). Agrupar aqui na tela reintroduziria pela interface o defeito que
 * a estrutura do banco acabou de resolver — por isso as linhas repetidas aparecem
 * separadas, com saldos independentes.
 */
export const ModalDevolucao = ({ aberto, vendaId, aoFechar }: Props) => {
  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [bloqueio, setBloqueio] = useState<string | null>(null);
  const [itens, setItens] = useState<ItemLinha[]>([]);
  const [motivo, setMotivo] = useState('');
  const [resumo, setResumo] = useState<any>(null);
  const [etapa, setEtapa] = useState<1 | 2>(1);
  const [devolucaoId, setDevolucaoId] = useState<number | null>(null);
  const [boletos, setBoletos] = useState<any[]>([]);

  // Verdadeiro só quando o rascunho já existia ao ABRIR a tela - não quando o próprio
  // operador acabou de gravar nesta sessão, caso em que ele sabe muito bem o que fez.
  const [rascunhoRetomado, setRascunhoRetomado] = useState(false);

  /**
   * [25/09] Quando sobra valor A FAVOR do cliente, o operador escolhe o destino ANTES de
   * confirmar: crédito na conta corrente dele, ou pagamento (contas a pagar à vista).
   *
   * Sobra quando ele já pagou mais do que a venda passou a valer depois da devolução.
   * Até a fase 2 esse dinheiro não ia para lugar nenhum - a devolução só regerava título
   * quando havia título EM ABERTO para cancelar, e numa venda quitada não há nenhum.
   */
  const [escolhendoDestino, setEscolhendoDestino] = useState(false);

  // Item 3 do relatorio de teste: forma e condicao escolhidas aqui, e nao herdadas da
  // venda. Repetir a condicao da venda gerava 5 boletos de R$ 30 para um saldo que o
  // cliente pagaria num PIX so.
  const [formaOptions, setFormaOptions] = useState<any[]>([]);
  const [condicaoOptions, setCondicaoOptions] = useState<any[]>([]);
  const [formaId, setFormaId] = useState<number | ''>('');
  const [condicaoId, setCondicaoId] = useState<number | ''>('');
  const [parcelas, setParcelas] = useState<any[]>([]);

  // Item 3 do relatorio de 23/09: a data de referencia é da DEVOLUÇÃO, padrão hoje, e o
  // operador altera à vontade. Antes os vencimentos saíam da data da venda de origem —
  // devolver em setembro uma venda de agosto nascia com as três parcelas vencidas.
  const [dataReferencia, setDataReferencia] = useState(hoje());

  const selecionados = itens.filter((i) => i.quantidade > 0);

  // A MESMA regra da guia Financeiro do pedido (shared/utils/helpers): forma à vista só
  // aceita condição de uma vez. É por causa dela que NÃO existe "Recebimento único" aqui:
  // marcar "à vista" já é escolher Pix/Dinheiro/Débito + A VISTA.
  const formaSelecionada = formaOptions.find((o) => Number(o.value) === Number(formaId));
  const formaEhAvista = descricaoEhFormaAvista(formaSelecionada?.label);
  const condicoes = condicoesPermitidas(condicaoOptions, formaEhAvista, formaId !== '');

  const limpar = () => {
    setItens([]);
    setMotivo('');
    setResumo(null);
    setBloqueio(null);
    setEtapa(1);
    setDevolucaoId(null);
    setBoletos([]);
    setFormaId('');
    setCondicaoId('');
    setParcelas([]);
    setDataReferencia(hoje());
  };

  // Trocar a forma para um tipo incompatível limpa a condição — senão o par inválido
  // seguiria escolhido mesmo com a opção desabilitada na lista. Mesmo comportamento da
  // guia Financeiro do pedido.
  const escolherForma = (valor: number | '') => {
    setFormaId(valor);

    if (valor === '' || condicaoId === '') return;

    const novaEhAvista = descricaoEhFormaAvista(
      formaOptions.find((o) => Number(o.value) === Number(valor))?.label
    );

    if (condicaoEhAvista(condicaoId) !== novaEhAvista) setCondicaoId('');
  };

  // As listas de forma e condicao, iguais as da guia Financeiro do pedido.
  useEffect(() => {
    if (!aberto) return;

    (async () => {
      const formas = await TipoPagamentoController.Listar({ Descricao: '' });
      if (formas.StatusCode === 200) {
        setFormaOptions((formas.Data || []).map((i: any) => ({ value: i.tpg_id, label: i.tpg_descricao })));
      }

      const condicoes = await CondicaoPagamentoController.Listar({
        Descricao: '', Quantidade: '', TipoParcelamento: '', TipoUtilizacao: '', QtdMaiorZero: 'S',
      });
      if (condicoes.StatusCode === 200) {
        setCondicaoOptions((condicoes.Data || []).map((i: any) => ({ value: i.cpg_id, label: i.cpg_descricao })));
      }
    })();
  }, [aberto]);

  // Ao abrir: pergunta se pode devolver e, podendo, carrega os itens.
  useEffect(() => {
    if (!aberto || !vendaId) return;

    limpar();
    setCarregando(true);

    (async () => {
      const permissao = await DevolucaoController.Permitida(vendaId);

      // O motivo vem da mesma validação que a confirmação usa, então a frase aqui é a
      // mesma que apareceria ao tentar confirmar. Botão apagado sem explicação não ajuda.
      if (permissao && permissao.pode_devolver !== 1 && permissao.pode_devolver !== true) {
        setBloqueio(permissao.motivo || 'Este pedido não pode ser devolvido.');
        setCarregando(false);
        return;
      }

      if (permissao?.devolucao_aberta) {
        setDevolucaoId(permissao.devolucao_aberta);
        setRascunhoRetomado(true);
      }

      const resposta = await DevolucaoController.Itens(vendaId, permissao?.devolucao_aberta);

      if (resposta.StatusCode !== 200) {
        setBloqueio(String(resposta.ErroDetalhado || 'Não foi possível carregar os itens.'));
        setCarregando(false);
        return;
      }

      setItens(
        (resposta.Data || []).map((i: any) => ({
          ...i,
          bonificado: i.bonificado === true || i.bonificado === 1,
          quantidade: Number(i.qtd_nesta_devolucao || 0),
        }))
      );
      setCarregando(false);
    })();
  }, [aberto, vendaId]);

  // A prévia é recalculada pela API a cada mudança, com um respiro de 400ms para não
  // disparar uma chamada por tecla digitada. É a MESMA conta que a confirmação faz —
  // prévia e confirmação calculadas por caminhos diferentes divergem um dia.
  const simular = useCallback(async () => {
    if (!vendaId || selecionados.length === 0) {
      setResumo(null);
      return;
    }

    const resposta = await DevolucaoController.Simular(
      vendaId,
      selecionados.map((i) => ({ idItemOrigem: i.id_item_origem, quantidade: i.quantidade })),
      condicaoId === '' ? null : Number(condicaoId),
      dataReferencia || null
    );

    if (resposta.StatusCode === 200) {
      setResumo(resposta.Data?.Resumo ?? null);
    } else {
      setResumo(null);
    }
  }, [vendaId, condicaoId, dataReferencia, JSON.stringify(selecionados.map((i) => [i.id_item_origem, i.quantidade]))]);

  useEffect(() => {
    const t = setTimeout(simular, 400);
    return () => clearTimeout(t);
  }, [simular]);

  /**
   * A previa das parcelas, pela MESMA rota que a guia Financeiro do pedido usa.
   *
   * A data de referencia vem do RESUMO (data_base), e nao direto do campo da tela: quem
   * confirma a base e o servidor, e e ele que a confirmacao vai usar. Previa e realidade
   * calculadas de bases diferentes batem hoje e divergem amanha - e o operador so descobre
   * quando o boleto vence no dia errado.
   */
  useEffect(() => {
    const valor = Number(resumo?.valor_a_gerar ?? 0);
    const base = resumo?.data_base ? String(resumo.data_base).substring(0, 10) : '';

    if (!condicaoId || valor <= 0 || !/^\d{4}-\d{2}-\d{2}$/.test(base)) {
      setParcelas([]);
      return;
    }

    let cancelado = false;
    (async () => {
      const r = await CondicaoPagamentoController.SimularParcelas({
        CondicaoPagamentoId: Number(condicaoId),
        ValorTotal: valor,
        DataReferencia: base,
        RecebimentoUnico: 'N',
        RepetirValor: 'N',
      });
      if (!cancelado) setParcelas(r.StatusCode === 200 && Array.isArray(r.Data) ? r.Data : []);
    })();

    return () => { cancelado = true; };
  }, [condicaoId, resumo?.valor_a_gerar, resumo?.data_base]);

  const alterarQuantidade = (idItem: number, valor: string) => {
    setItens((atual) =>
      atual.map((i) => {
        if (i.id_item_origem !== idItem) return i;

        const n = Number(valor.replace(/\D/g, '') || 0);
        // O saldo é o teto. O servidor revalida por conta própria — o limite aqui é
        // conveniência, não controle.
        return { ...i, quantidade: Math.min(n, i.saldo_devolvivel) };
      })
    );
  };

  // Etapa 1 -> 2: grava o rascunho e busca os boletos que serão baixados.
  const continuar = async () => {
    if (!vendaId) return;

    if (selecionados.length === 0) {
      return toast('Informe a quantidade de pelo menos um item.', { type: 'info' });
    }

    if (!motivo.trim()) {
      return toast('O motivo da devolução é obrigatório.', { type: 'info' });
    }

    /* [25/09] So exige forma e condicao quando ha saldo a cobrar.
     *
     * Este bloco ficou para tras na primeira correcao: eu tirei a exigencia do
     * `disabled` do botao e esqueci a validacao AQUI. Resultado: o botao acendia e o
     * clique devolvia "Informe a forma de pagamento" - pior que o travamento anterior,
     * porque agora a tela dizia que dava para continuar e depois recusava.
     *
     * Duas travas para a mesma regra sempre divergem. As duas leem `naoHaQueCobrar`. */
    if (!naoHaQueCobrar && !formaId) {
      return toast('Informe a forma de pagamento.', { type: 'info' });
    }

    if (!naoHaQueCobrar && !condicaoId) {
      return toast('Informe a condição de parcelamento.', { type: 'info' });
    }

    if (!/^\d{4}-\d{2}-\d{2}$/.test(dataReferencia)) {
      return toast('Informe a data de referência.', { type: 'info' });
    }

    setSalvando(true);

    const gravacao = await DevolucaoController.Gravar({
      VendaId: vendaId,
      DevolucaoId: devolucaoId,
      Motivo: motivo.trim(),
      Itens: selecionados.map((i) => ({ idItemOrigem: i.id_item_origem, quantidade: i.quantidade })),
      TipoPagamentoId: Number(formaId),
      CondicaoPagamentoId: Number(condicaoId),
      DataReferencia: dataReferencia,
    });

    if (gravacao.StatusCode !== 200) {
      setSalvando(false);
      return toast(String(gravacao.ErroDetalhado || 'Não foi possível gravar a devolução.'), {
        type: 'error',
        autoClose: 10000,
      });
    }

    const novoId = gravacao.Data?.DevolucaoId ?? devolucaoId;
    setDevolucaoId(novoId);

    const listaBoletos = await DevolucaoController.Boletos(novoId);
    setBoletos(listaBoletos.StatusCode === 200 ? listaBoletos.Data || [] : []);

    setSalvando(false);
    setEtapa(2);
  };

  // Positivo = a empresa deve ao cliente. Vem da simulação, calculado no banco - a tela
  // não refaz a conta, para não haver duas versões dela.
  const saldoAFavor = Number(resumo?.saldo_a_favor_cliente ?? 0);

  /**
   * [07/10, script 173] O crédito que a venda consumiu, e quanto dele volta para a conta
   * corrente do cliente nesta devolução.
   *
   * Até o 173 a devolução não sabia que crédito existia: comparava a mercadoria pelo
   * valor CHEIO com os títulos, que já vêm abatidos do crédito. Venda de 200 com 100 de
   * crédito era recusada na devolução total ("o valor devolvido é maior que o total do
   * pedido") e, na parcial, anunciava "Total do pedido: 100".
   *
   * `?? 0` e não `|| 0`: zero é um valor legítimo aqui (venda sem crédito), e `||` o
   * trocaria por zero do mesmo jeito — mas a distinção importa se um dia o campo virar
   * opcional. É a armadilha que apareceu na tela de Conta Bancária em 06/10.
   */
  const creditoUsado = Number(resumo?.credito_usado ?? 0);
  const creditoADevolver = Number(resumo?.credito_a_devolver ?? 0);

  /**
   * [08/10, script 178] A parte do saldo a favor que é DINHEIRO pago - a única sobre a
   * qual o operador escolhe "gerar crédito ou pagar".
   *
   * Desde o 173 o crédito conta como pagamento, então o saldo a favor passou a incluir o
   * crédito que volta. Pedido do Deivid: o que veio de crédito volta SEMPRE como crédito
   * (a frase acima avisa); perguntar sobre ele deixava o operador transformar crédito em
   * contas a pagar em dinheiro. Devolução total de venda paga só com crédito: dinheiro
   * zero, nenhuma pergunta. O banco aplica a mesma regra (178), também para chamada direta.
   */
  const dinheiroAFavor = Math.max(0, saldoAFavor - creditoADevolver);

  /**
   * Não sobrou nada a cobrar do cliente: devolução total, ou ele já pagou tudo.
   *
   * [25/09] Nesse caso forma e condição não têm função nenhuma - não haverá parcela para
   * gerar -, mas a tela continuava exigindo as duas e o Continuar ficava apagado. Beco
   * sem saída: o operador não consegue prosseguir preenchendo campos que não mudam nada.
   * É a mesma forma do `financeiro.cpg_id` obrigatório que travou as vendas geradas por
   * Ordem de Serviço em 07/08.
   *
   * O `NaN` quando o campo não vem é proposital: API antiga, sem saldo_a_cobrar, cai no
   * comportamento de antes (campos exigidos). Errar para o lado de pedir demais é
   * recuperável; para o lado de pedir de menos, a devolução gravaria sem condição.
   */
  const saldoACobrar = Number(resumo?.saldo_a_cobrar ?? NaN);
  const naoHaQueCobrar = !!resumo && Number.isFinite(saldoACobrar) && saldoACobrar <= 0.004;

  /**
   * [25/09] Quanto o cliente JÁ PAGOU. A procedure sempre mandou (`valor_pago`) e a tela
   * nunca mostrou — e é o número que decide se há algo a devolver a ele.
   *
   * Sem ele a tela ficava ambígua no caso mais comum: "Total devolvido R$ 1.291,90" ao
   * lado de "Resta a cobrar R$ 0,00" parece dizer que R$ 1.291,90 vão para algum lugar.
   * Não vão — é o valor das mercadorias que voltam ao estoque, e as parcelas canceladas
   * eram dívida do cliente, não dinheiro dele. Em dois testes seguidos isso foi lido como
   * "o modal do crédito não apareceu, o deploy falhou".
   */
  const jaPago = Number(resumo?.valor_pago ?? 0);
  const nadaFoiPago = jaPago <= 0.004;

  const confirmar = async () => {
    if (!devolucaoId) return;

    // Há valor a favor do cliente: ele precisa dizer o que fazer com ele antes de
    // confirmar. Escolher DEPOIS seria tarde - a confirmação é uma transação só.
    if (dinheiroAFavor > 0.004) {
      setEscolhendoDestino(true);
      return;
    }

    await executarConfirmacao();
  };

  const executarConfirmacao = async (destino?: 'CREDITO' | 'PAGAR') => {
    if (!devolucaoId) return;

    setEscolhendoDestino(false);
    setSalvando(true);
    const resposta = await DevolucaoController.Confirmar(devolucaoId, destino);
    setSalvando(false);

    if (resposta.StatusCode !== 200) {
      // A mensagem traz o motivo do banco quando a baixa foi recusada. Tempo longo de
      // leitura: é um texto que o operador precisa levar para alguém.
      return toast(String(resposta.ErroDetalhado || 'Não foi possível confirmar a devolução.'), {
        type: 'error',
        autoClose: 15000,
      });
    }

    toast('Devolução confirmada.', { type: 'success' });
    aoFechar(true);
  };

  const fechar = () => aoFechar(false);

  return (
    <Dialog open={aberto} onClose={fechar} maxWidth="lg" fullWidth>
      {/* Item 4 do relatorio de 23/09: a tela estava "toda branca". O vermelho é o do
          próprio sistema (theme.palette.secondary = #E10511), não uma cor nova. */}
      <DialogTitle
        className="!font-bold !text-md"
        sx={{ bgcolor: 'secondary.main', color: 'common.white' }}
      >
        {etapa === 1 ? `Devolução do pedido ${vendaId ?? ''}` : 'Confirmar devolução'}
      </DialogTitle>

      <DialogContent dividers>
        {carregando && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {!carregando && bloqueio && <Alert severity="warning">{bloqueio}</Alert>}

        {!carregando && !bloqueio && etapa === 1 && (
          <>
            {/**
             * [25/09] Dizer que esta é a MESMA devolução, e não uma nova.
             *
             * Quando a venda já tem um rascunho em aberto, a tela continua esse rascunho
             * (a procedure devolve o id em `devolucao_aberta`, e as quantidades vêm
             * preenchidas). Só que a tela ficava idêntica à de uma devolução nova — e no
             * teste isso foi lido como "o sistema deixou devolver duas vezes". Não deixou:
             * é o mesmo documento sendo editado, e a confirmação revalida o saldo.
             *
             * O número aparece porque é ele que permite conferir na listagem.
             */}
            {rascunhoRetomado && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Você está continuando a devolução <strong>{devolucaoId}</strong>, que ficou
                em aberto para este pedido — não é uma devolução nova. As quantidades abaixo
                são as que já estavam marcadas nela.
              </Alert>
            )}

            <Table size="small" sx={cabecalhoDaTabela}>
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell align="right">Vendido</TableCell>
                  <TableCell align="right">Já devolvido</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                  <TableCell align="right">Devolver</TableCell>
                  <TableCell align="right">Valor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {itens.map((i) => (
                  <TableRow key={i.id_item_origem}>
                    <TableCell>
                      {i.descricao}
                      {i.bonificado && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          bonificado — volta ao estoque, devolve R$ 0,00
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{i.qtd_original}</TableCell>
                    <TableCell align="right">{i.qtd_devolvida}</TableCell>
                    <TableCell align="right">{i.saldo_devolvivel}</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      <TextField
                        size="small"
                        value={i.quantidade || ''}
                        disabled={i.saldo_devolvivel <= 0}
                        onChange={(e) => alterarQuantidade(i.id_item_origem, e.target.value)}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      {formatCurrency(
                        i.bonificado || !i.qtd_original
                          ? 0
                          : (i.vndprod_total * i.quantidade) / i.qtd_original
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box mt={3}>
              <TextField
                label="Motivo da devolução"
                multiline
                rows={3}
                fullWidth
                required
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
                inputProps={{ maxLength: 700 }}
                helperText={`${motivo.length}/700 — este texto vai para as Informações Complementares da nota de devolução`}
                sx={{ '& textarea': { textTransform: 'none' } }}
              />
            </Box>

            {/* Item 3 do relatorio de teste: a escolha fica AQUI, na mesma tela dos itens,
                com a previa das parcelas logo abaixo - como na guia Financeiro do pedido. */}
            <Paper variant="outlined" sx={{ mt: 3, p: 2, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" gutterBottom>
                {naoHaQueCobrar ? 'Cobrança' : 'Como o saldo será cobrado'}
              </Typography>

              {naoHaQueCobrar && (
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Não sobra nada a cobrar deste pedido, então não há parcela a gerar e a
                  forma de pagamento não se aplica. A data de referência continua valendo:
                  é dela que sai a data do documento.
                  {/* [25/09] Dizer também por que NÃO haverá pergunta sobre crédito. A
                      ausência do modal é indistinguível de defeito para quem não conhece
                      a regra — e foi assim que dois testes acabaram reportados como
                      "não subiu". */}
                  {nadaFoiPago && (
                    <>
                      {' '}
                      <strong>
                        Nada a devolver ao cliente: não houve pagamento neste pedido.
                      </strong>{' '}
                      As parcelas canceladas eram dívida dele, não dinheiro já entregue —
                      por isso não haverá crédito nem contas a pagar.
                    </>
                  )}
                </Typography>
              )}

              <Box display="flex" gap={2} flexWrap="wrap">
                {!naoHaQueCobrar && (
                <>
                <Select
                  select
                  size="small"
                  label="Forma de pagamento *"
                  value={formaId}
                  onChange={(e) => escolherForma(e.target.value === '' ? '' : Number(e.target.value))}
                  sx={{ minWidth: 260, bgcolor: 'common.white' }}
                >
                  {formaOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>

                <Select
                  select
                  size="small"
                  label="Condição de parcelamento *"
                  value={condicaoId}
                  onChange={(e) => setCondicaoId(e.target.value === '' ? '' : Number(e.target.value))}
                  sx={{ minWidth: 300, bgcolor: 'common.white' }}
                  helperText={
                    formaId === ''
                      ? 'Informe a forma de pagamento primeiro.'
                      : formaEhAvista
                        ? 'Forma à vista: só cabe condição de uma vez.'
                        : 'Não precisa repetir o parcelamento da venda'
                  }
                >
                  {/* Opção incompatível com a forma aparece, mas desabilitada: some da
                      lista o operador não entende por quê. */}
                  {condicoes.map((o: any) => (
                    <MenuItem key={o.value} value={o.value} disabled={o.disabled}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
                </>
                )}

                <Select
                  type="date"
                  size="small"
                  label="Data de referência *"
                  value={dataReferencia}
                  onChange={(e) => setDataReferencia(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 200, bgcolor: 'common.white' }}
                  helperText="Os vencimentos contam a partir dela"
                />
              </Box>
            </Paper>

            {parcelas.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Parcelas que serão geradas para o saldo
                </Typography>
                <Table size="small" sx={cabecalhoDaTabela}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Parcela</TableCell>
                      <TableCell>Vencimento</TableCell>
                      <TableCell align="right">Valor</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parcelas.map((p: any) => {
                      const venc = String(p.data_vencimento ?? '').substring(0, 10);
                      // Vencimento no passado acontece quando a venda e antiga e a condicao
                      // escolhida tem prazo curto. Nao "empurro para hoje" por conta propria
                      // - so aviso, e o operador escolhe outra condicao se quiser.
                      const atrasada = venc !== '' && venc < new Date().toISOString().substring(0, 10);
                      return (
                        <TableRow key={p.parcela_numero}>
                          <TableCell>{p.parcela_numero}</TableCell>
                          <TableCell sx={atrasada ? { color: 'error.main' } : undefined}>
                            {venc ? venc.split('-').reverse().join('/') : '-'}
                            {atrasada && ' (vencida)'}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(p.valor)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}

            {resumo && (
              <Box mt={3}>
                <Divider />
                <Box display="flex" gap={4} flexWrap="wrap" mt={2}>
                  <Resumo rotulo="Total do pedido" valor={formatCurrency(resumo.total_venda)} />
                  <Resumo rotulo="Total devolvido" valor={formatCurrency(resumo.total_devolvido)} />
                  <Resumo rotulo="Já pago pelo cliente" valor={formatCurrency(jaPago)} />
                  {/* [25/09] Passou a ler saldo_a_cobrar, e não valor_liquido_apos.
                      São coisas diferentes desde o script 109: valor_liquido_apos é
                      quanto a VENDA passa a valer; saldo_a_cobrar é o que o cliente
                      ainda deve, já descontado o que ele pagou. Com o rótulo antigo, uma
                      venda paga mostrava "resta a cobrar" um valor que ninguém iria
                      cobrar. */}
                  <Resumo rotulo="Resta a cobrar" valor={formatCurrency(resumo.saldo_a_cobrar ?? 0)} />
                  {dinheiroAFavor > 0.004 && (
                    <Resumo rotulo="A favor do cliente" valor={formatCurrency(dinheiroAFavor)} />
                  )}
                  {/* [07/10] Só aparecem quando a venda usou crédito — numa venda normal
                      seriam duas colunas de zero, e resumo cheio de zero ninguém lê. */}
                  {creditoUsado > 0.004 && (
                    <Resumo
                      rotulo="Crédito usado na venda"
                      valor={formatCurrency(creditoUsado)}
                    />
                  )}
                  {creditoADevolver > 0.004 && (
                    <Resumo
                      rotulo="Crédito que volta ao cliente"
                      valor={formatCurrency(creditoADevolver)}
                    />
                  )}
                  <Resumo rotulo="Parcelas canceladas" valor={String(resumo.titulos_a_cancelar ?? 0)} />
                  <Resumo rotulo="Parcelas novas" valor={String(resumo.parcelas_a_gerar ?? 0)} />
                  <Resumo rotulo="Boletos a baixar" valor={String(resumo.boletos_a_baixar ?? 0)} />
                </Box>
              </Box>
            )}
          </>
        )}

        {!carregando && etapa === 2 && (
          <>
            {boletos.length > 0 ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <strong>
                  {boletos.length} boleto(s) serão baixados no banco ao confirmar, e essa baixa não
                  tem volta.
                </strong>{' '}
                Se algum banco recusar a baixa, nada será confirmado.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                Nenhum boleto registrado para este pedido — nada será baixado no banco.
              </Alert>
            )}

            {boletos.length > 0 && (
              <Table size="small" sx={cabecalhoDaTabela}>
                <TableHead>
                  <TableRow>
                    <TableCell>Parcela</TableCell>
                    <TableCell>Vencimento</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Título no banco</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boletos.map((b: any) => (
                    <TableRow key={b.cre_id}>
                      <TableCell>{b.cre_observacao || b.cre_id}</TableCell>
                      <TableCell>
                        {b.cre_vencimento ? new Date(b.cre_vencimento).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell align="right">{formatCurrency(b.cre_valor)}</TableCell>
                      <TableCell>{b.cre_numerotitulocobranca || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {resumo && (
              <Box mt={3}>
                <Typography variant="body2">
                  Serão devolvidos <strong>{formatCurrency(resumo.total_devolvido)}</strong> em{' '}
                  {selecionados.length} item(ns). O pedido passa a ter{' '}
                  <strong>{formatCurrency(resumo.saldo_a_cobrar ?? 0)}</strong> a cobrar, em{' '}
                  {resumo.parcelas_a_gerar ?? 0} parcela(s) nova(s).
                </Typography>
                {dinheiroAFavor > 0.004 && (
                  <Typography variant="body2" mt={1}>
                    O cliente já pagou mais do que o pedido passa a valer:{' '}
                    <strong>{formatCurrency(dinheiroAFavor)}</strong> ficam a favor dele. Ao
                    confirmar, você escolhe entre gerar crédito ou pagar.
                  </Typography>
                )}
                {/* [07/10] A devolução do crédito é silenciosa no banco — vai direto para
                    a conta corrente do cliente. Se não for dita aqui, ninguém descobre
                    que aconteceu, e é dinheiro. */}
                {creditoADevolver > 0.004 && (
                  <Typography variant="body2" mt={1}>
                    Esta venda usou <strong>{formatCurrency(creditoUsado)}</strong> de
                    crédito do cliente. Como parte da mercadoria volta,{' '}
                    <strong>{formatCurrency(creditoADevolver)}</strong> retornam para a
                    conta corrente dele ao confirmar.
                  </Typography>
                )}
                {/* [25/09] O contrário também precisa ser dito, e aqui: esta é a última
                    tela antes do Confirmar, onde a pergunta "e o dinheiro do cliente?"
                    aparece. Silêncio aqui é o que fez o teste concluir que faltava
                    funcionalidade. */}
                {saldoAFavor <= 0.004 && nadaFoiPago && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Não há valor a devolver ao cliente: ele não chegou a pagar nada deste
                    pedido. Não será gerado crédito nem contas a pagar.
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary" mt={1}>
                  Os produtos voltam ao estoque na confirmação.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button title="Fechar" variant="secondary" onClick={fechar} disabled={salvando} />

        {!bloqueio && etapa === 1 && (
          <Button
            title={salvando ? 'Aguarde...' : 'Continuar'}
            variant="primary"
            onClick={continuar}
            disabled={
              salvando ||
              carregando ||
              selecionados.length === 0 ||
              // Só exige forma e condição quando há saldo a cobrar: sem isso não há
              // parcela para gerar, e os campos não decidem nada.
              (!naoHaQueCobrar && (!formaId || !condicaoId))
            }
          />
        )}

        {!bloqueio && etapa === 2 && (
          <>
            <Button
              title="Voltar"
              variant="secondary"
              onClick={() => setEtapa(1)}
              disabled={salvando}
            />
            <Button
              title={salvando ? 'Confirmando...' : 'Confirmar devolução'}
              variant="primary"
              onClick={confirmar}
              disabled={salvando}
            />
          </>
        )}
      </DialogActions>

      {/* [25/09] A escolha do destino do saldo a favor do cliente.

          Fica num diálogo próprio, e não como um campo na tela, porque só existe quando
          sobra valor - e na maioria das devoluções não sobra. Um campo permanente para
          um caso minoritário vira ruído que o operador aprende a ignorar.

          Sem botão de fechar no canto: as duas saídas são decisões, e "Cancelar" volta
          para a tela sem confirmar nada. Fechar no X deixaria a dúvida de se a devolução
          foi confirmada ou não. */}
      <Dialog open={escolhendoDestino} onClose={() => setEscolhendoDestino(false)} maxWidth="sm" fullWidth>
        <DialogTitle>O que fazer com {formatCurrency(dinheiroAFavor)} a favor do cliente?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            O cliente pagou mais do que o pedido passa a valer depois desta devolução.
          </Typography>
          {/* [08/10] O crédito não entra nesta escolha: volta sempre como crédito. */}
          {creditoADevolver > 0.004 && (
            <Typography variant="body2" mt={1}>
              Os <strong>{formatCurrency(creditoADevolver)}</strong> de crédito usados na
              venda voltam para a conta corrente dele em qualquer caso.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" mt={2}>
            <strong>Gerar crédito</strong> lança o valor na conta corrente dele, para
            abater em uma compra futura.
          </Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            <strong>Pagar o cliente</strong> gera um contas a pagar à vista, com
            vencimento na data de referência da devolução.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            title="Cancelar"
            variant="secondary"
            onClick={() => setEscolhendoDestino(false)}
            disabled={salvando}
          />
          <Button
            title="Gerar crédito"
            variant="secondary"
            onClick={() => executarConfirmacao('CREDITO')}
            disabled={salvando}
          />
          <Button
            title="Pagar o cliente"
            variant="primary"
            onClick={() => executarConfirmacao('PAGAR')}
            disabled={salvando}
          />
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

/**
 * Item 4 do relatorio de 23/09: "a tela está toda branca".
 * Cabeçalho de tabela com fundo cinza e texto em negrito — é o que separa dado de rótulo
 * quando a caixa inteira é branca.
 */
const cabecalhoDaTabela = {
  '& thead th': { bgcolor: 'grey.100', fontWeight: 600 },
};

const Resumo = ({ rotulo, valor }: { rotulo: string; valor: string }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" display="block">
      {rotulo}
    </Typography>
    <Typography variant="body1" fontWeight={600}>
      {valor}
    </Typography>
  </Box>
);
